import { KeyboardEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TabItem } from '@/components/Tab/TabBar';
import { shortenNumber } from '@/utils';
import { FOCUS_RING, TAB_LABEL_KEYS } from './toolbarConfig';

/** Order buckets ("views") — a light segmented control; the active view is the yellow one. */
interface Props {
  tabs: TabItem[];
  activeTab: string;
  onChange: (key: string) => void;
  /** Red dot on this view (Problematic → orders that need action). */
  alert?: { key: string; count: number };
}

const tabClass = (active: boolean) =>
  [
    'inline-flex items-center gap-1.5 h-8 px-2.5 shrink-0 whitespace-nowrap border-0 cursor-pointer text-[13px] font-semibold rounded-lg transition-colors',
    FOCUS_RING,
    active
      ? 'bg-sd-primary text-sd-primary-foreground'
      : 'bg-transparent text-sd-muted-foreground hover:text-sd-foreground hover:bg-sd-muted',
  ].join(' ');

const OrdersViewTabs = ({ tabs, activeTab, onChange, alert }: Props) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the selected bucket fully visible in the scroll strip (narrow screens).
  // Re-run when counts arrive — they widen the tabs. Clear of the end fade.
  const badgeKey = tabs.map((tab) => tab.badge ?? '').join();
  useEffect(() => {
    const list = listRef.current;
    const el = list?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!list || !el) return;
    const l = list.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    if (r.left < l.left) list.scrollLeft -= l.left - r.left + 40;
    else if (r.right > l.right) list.scrollLeft += r.right - l.right + 40;
  }, [activeTab, badgeKey]);

  // WAI-ARIA tabs: arrows move + select (mirrored in RTL), Home/End jump.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const keys = tabs.map((tab) => String(tab.key));
    const i = keys.indexOf(activeTab);
    const rtl = document.documentElement.dir === 'rtl';
    const step = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 }[e.key];
    let next: number | undefined;
    if (step) next = (i + step + keys.length) % keys.length;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = keys.length - 1;
    if (next === undefined) return;
    e.preventDefault();
    onChange(keys[next]);
    listRef.current?.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
  };

  return (
    <div
      ref={listRef}
      role='tablist'
      aria-label={String(t('orders'))}
      onKeyDown={onKeyDown}
      className='flex items-center gap-0.5 min-w-0 overflow-x-auto max-lg:pe-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden max-lg:[mask-image:linear-gradient(to_right,black_calc(100%-32px),transparent)] max-lg:rtl:[mask-image:linear-gradient(to_left,black_calc(100%-32px),transparent)]'
    >
      {tabs.map((tab) => {
        const key = String(tab.key);
        const active = key === activeTab;
        const alerting = alert && alert.key === key && alert.count > 0;
        return (
          <button
            key={key}
            type='button'
            role='tab'
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(key)}
            className={tabClass(active)}
          >
            {String(t(TAB_LABEL_KEYS[key] ?? key))}
            {alerting ? (
              <span
                className='w-1.5 h-1.5 rounded-full bg-red-500 shrink-0'
                title={String(t('needs_action_count', { n: alert.count }))}
              >
                <span className='sr-only'>
                  {String(t('needs_action_count', { n: alert.count }))}
                </span>
              </span>
            ) : null}
            {tab.badge !== undefined && tab.badge > 0 ? (
              <span
                className={`text-[12px] font-medium tabular-nums ${
                  active ? 'text-sd-primary-foreground/70' : 'text-sd-muted-foreground/80'
                }`}
              >
                {shortenNumber(tab.badge)}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

export default OrdersViewTabs;
